import React, { useState, useMemo } from 'react';
import { Col, Dropdown, DropdownMenu, DropdownToggle, Nav, NavItem, NavLink, Row } from 'reactstrap';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import SimpleBar from "simplebar-react";
import { useAlertesQuery } from '../../hooks/queries/useRoadAlertQueries';
import { useAuthStore } from '../../stores/useAuthStore';
import moment from 'moment';
import 'moment/locale/fr';

// Types de notifications
interface NotificationItem {
  id: number;
  type: 'nouvelle' | 'affectation' | 'statut' | 'urgente';
  title: string;
  message: string;
  time: string;
  alerteId: number;
  icon: string;
  iconColor: string;
  badgeColor: string;
}

const NotificationDropdown = () => {
  const { user } = useAuthStore();
  const userId = user?.userId || user?._id || user?.id;

  // Dropdown Toggle
  const [isNotificationDropdown, setIsNotificationDropdown] = useState<boolean>(false);
  const toggleNotificationDropdown = () => {
    setIsNotificationDropdown(!isNotificationDropdown);
  };

  // Tab
  const [activeTab, setActiveTab] = useState('1');
  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  // Récupération des alertes récentes pour générer les notifications
  const recentAlertesQuery = useAlertesQuery(
    {
      user: userId ? String(userId) : "",
      data: {},
      page: 0,
      size: 20,
    },
    {
      enabled: !!userId,
    }
  );

  // Génération des notifications basées sur les alertes récentes
  const notifications = useMemo<NotificationItem[]>(() => {
    const items = recentAlertesQuery.data?.items;
    if (!items || !Array.isArray(items)) return [];

    const alertes = items as any[];
    const notifs: NotificationItem[] = [];

    alertes.forEach((alerte: any) => {
      const dateCreation = alerte.dateCreation ? new Date(alerte.dateCreation) : new Date();
      const timeAgo = moment(dateCreation).locale('fr').fromNow();

      // Notification pour nouvelles alertes (créées il y a moins de 24h)
      const hoursSinceCreation = (Date.now() - dateCreation.getTime()) / (1000 * 60 * 60);
      if (hoursSinceCreation < 24) {
        const isUrgent = alerte.priorite?.idPriorite === 1 || alerte.libellePriorite?.toLowerCase().includes('urgent');
        
        if (isUrgent) {
          notifs.push({
            id: alerte.idAlerte * 1000 + 1,
            type: 'urgente',
            title: 'Alerte urgente',
            message: `Nouvelle alerte urgente : ${alerte.libelleAlerte || 'Sans titre'}`,
            time: timeAgo,
            alerteId: alerte.idAlerte,
            icon: 'ri-alarm-warning-line',
            iconColor: 'danger',
            badgeColor: 'danger',
          });
        } else {
          notifs.push({
            id: alerte.idAlerte * 1000 + 2,
            type: 'nouvelle',
            title: 'Nouvelle alerte',
            message: `${alerte.libelleAlerte || 'Sans titre'} - ${alerte.libelleCommune || ''}`,
            time: timeAgo,
            alerteId: alerte.idAlerte,
            icon: 'ri-road-map-line',
            iconColor: 'primary',
            badgeColor: 'primary',
          });
        }
      }

      // Notification pour alertes non affectées (statut 1)
      if (alerte.statutAlerte?.idStatutAlerte === 1 || alerte.idStatutAlerte === 1) {
        notifs.push({
          id: alerte.idAlerte * 1000 + 3,
          type: 'affectation',
          title: 'Alerte non affectée',
          message: `L'alerte "${alerte.libelleAlerte || 'Sans titre'}" nécessite une affectation`,
          time: timeAgo,
          alerteId: alerte.idAlerte,
          icon: 'ri-user-unfollow-line',
          iconColor: 'warning',
          badgeColor: 'warning',
        });
      }

      // Notification pour changements de statut récents
      if (alerte.statutAlerte?.idStatutAlerte === 6 || alerte.idStatutAlerte === 6) {
        notifs.push({
          id: alerte.idAlerte * 1000 + 4,
          type: 'statut',
          title: 'Alerte en cours',
          message: `Les travaux ont démarré pour "${alerte.libelleAlerte || 'Sans titre'}"`,
          time: timeAgo,
          alerteId: alerte.idAlerte,
          icon: 'ri-road-map-line',
          iconColor: 'info',
          badgeColor: 'info',
        });
      }
    });

    // Trier par date (plus récentes en premier) et limiter à 10
    return notifs
      .sort((a, b) => {
        const aTime = alertes.find((al: any) => al.idAlerte === a.alerteId)?.dateCreation || '';
        const bTime = alertes.find((al: any) => al.idAlerte === b.alerteId)?.dateCreation || '';
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      })
      .slice(0, 10);
  }, [recentAlertesQuery.data?.items]);

  // Filtrer les notifications par type
  const filteredNotifications = useMemo(() => {
    switch (activeTab) {
      case '2':
        return notifications.filter(n => n.type === 'affectation');
      case '3':
        return notifications.filter(n => n.type === 'urgente' || n.type === 'statut');
      default:
        return notifications;
    }
  }, [notifications, activeTab]);

  const unreadCount = notifications.length;
  const urgentCount = notifications.filter(n => n.type === 'urgente').length;
  const affectationCount = notifications.filter(n => n.type === 'affectation').length;

  const getNotificationIcon = (icon: string, color: string) => {
    const colorClass = `bg-${color}-subtle text-${color}`;
    return (
      <span className={`avatar-title ${colorClass} rounded-circle fs-16`}>
        <i className={icon}></i>
      </span>
    );
  };

  return (
    <React.Fragment>
      <Dropdown isOpen={isNotificationDropdown} toggle={toggleNotificationDropdown} className="topbar-head-dropdown ms-1 header-item">
        <DropdownToggle type="button" tag="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle">
          <i className='bx bx-bell fs-22'></i>
          {unreadCount > 0 && (
            <span className="position-absolute topbar-badge translate-middle badge rounded-pill bg-danger">
              {unreadCount > 9 ? '9+' : unreadCount}
              <span className="visually-hidden">notifications non lues</span>
            </span>
          )}
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
          <div className="dropdown-head bg-primary bg-pattern rounded-top">
            <div className="p-3">
              <Row className="align-items-center">
                <Col>
                  <h6 className="m-0 fs-16 fw-semibold text-white">Notifications</h6>
                </Col>
                {unreadCount > 0 && (
                  <div className="col-auto dropdown-tabs">
                    <span className="badge bg-light-subtle text-body fs-13">{unreadCount} Nouvelle{unreadCount > 1 ? 's' : ''}</span>
                  </div>
                )}
              </Row>
            </div>

            <div className="px-2 pt-2">
              <Nav className="nav-tabs dropdown-tabs nav-tabs-custom">
                <NavItem>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === '1' })}
                    onClick={(e) => { e.preventDefault(); toggleTab('1'); }}
                  >
                    Toutes ({unreadCount})
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === '2' })}
                    onClick={(e) => { e.preventDefault(); toggleTab('2'); }}
                  >
                    Affectations ({affectationCount})
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === '3' })}
                    onClick={(e) => { e.preventDefault(); toggleTab('3'); }}
                  >
                    Urgentes ({urgentCount})
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </div>

          <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
            {filteredNotifications.length === 0 ? (
              <div className="text-center p-4">
                <div className="mb-3">
                  <i className="ri-notification-off-line fs-48 text-muted"></i>
                </div>
                <h6 className="fs-14 fw-semibold">Aucune notification</h6>
                <p className="text-muted fs-13 mb-0">Vous n'avez aucune nouvelle notification</p>
              </div>
            ) : (
              <>
                {filteredNotifications.map((notification) => (
                  <div key={notification.id} className="text-reset notification-item d-block dropdown-item position-relative">
                    <div className="d-flex">
                      <div className="avatar-xs me-3 flex-shrink-0">
                        {getNotificationIcon(notification.icon, notification.iconColor)}
                      </div>
                      <div className="flex-grow-1">
                        <Link 
                          to={`/alertes/${notification.alerteId}`} 
                          className="stretched-link"
                          onClick={() => setIsNotificationDropdown(false)}
                        >
                          <h6 className="mt-0 mb-1 lh-base fs-13">
                            <span className={`badge bg-${notification.badgeColor}-subtle text-${notification.badgeColor} me-1`}>
                              {notification.title}
                            </span>
                          </h6>
                          <p className="mb-1 fs-13 text-muted">{notification.message}</p>
                          <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                            <span><i className="mdi mdi-clock-outline"></i> {notification.time}</span>
                          </p>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="my-3 text-center">
                  <Link 
                    to="/dashboard-alertes" 
                    className="btn btn-soft-primary waves-effect waves-light"
                    onClick={() => setIsNotificationDropdown(false)}
                  >
                    Voir toutes les alertes <i className="ri-arrow-right-line align-middle"></i>
                  </Link>
                </div>
              </>
            )}
          </SimpleBar>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default NotificationDropdown;
